/**
 * @module ui/scroll-menu
 */
import ee from "lib/eventemitter";
import { toArray } from "utils/utils";

const SPEED_MULTIPLIER = 1.11;
const MAX_MOVE_SPEED = 20;
const INTERVAL_TIME = 50;
const MENU_JUMP_VALUE = 100;
const CLICK_THRESHOLD = 200;
const MOVE_START_SPEED = 2;

// Prototype object for the scroll menu component

const ScrollMenuPrototype = {
  menuList: null,
  arrowLeft: null,
  arrowRight: null,
  moveSpeed: null,
  mouseIsDown: false,
  mouseLongPress: false,
  scrollInterval: null,
  clickTimeout: null,

  /**
   * @method init
   * @description Adds click/touch event handlers to the scroll menu's buttons
   * @param {HTMLElement} rootElement - the root scroll menu element
   */
  init(rootElement) {
    if (!rootElement || !rootElement.classList.contains("c-scroll-menu")) {
      return;
    }
    this.rootElement = rootElement;
    this.menuNav = rootElement.querySelector(".c-scroll-menu__nav");
    this.menuList = rootElement.querySelector(".c-scroll-menu__list");
    this.arrowLeft = rootElement.querySelector(".c-scroll-menu__arrow--left");
    this.arrowRight = rootElement.querySelector(".c-scroll-menu__arrow--right");
    this.moveSpeed = MOVE_START_SPEED;

    if (!this.menuNav || !this.menuList || !this.arrowLeft || !this.arrowRight) {
      return;
    }

    // Left arrow click
    this.arrowLeft.addEventListener("mousedown", () => {
      this.onLeftArrowTouch();
    });
    this.arrowLeft.addEventListener("touchstart", () => {
      this.onLeftArrowTouch();
    }, { passive: true });

    // Left arrow release
    this.arrowLeft.addEventListener("mouseup", () => {
      this.onLeftArrowRelease();
    });
    this.arrowLeft.addEventListener("mouseleave", () => {
      this.onLeftArrowRelease();
    });
    this.arrowLeft.addEventListener("touchend", () => {
      this.onLeftArrowRelease();
    });
    this.arrowLeft.addEventListener("touchcancel", () => {
      this.onLeftArrowRelease();
    });

    // Right arrow click
    this.arrowRight.addEventListener("mousedown", () => {
      this.onRightArrowTouch();
    });
    this.arrowRight.addEventListener("touchstart", () => {
      this.onRightArrowTouch();
    }, { passive: true });

    // Right arrow release
    this.arrowRight.addEventListener("mouseup", () => {
      this.onRightArrowRelease();
    });
    this.arrowRight.addEventListener("mouseleave", () => {
      this.onRightArrowRelease();
    });
    this.arrowRight.addEventListener("touchend", () => {
      this.onRightArrowRelease();
    });
    this.arrowRight.addEventListener("touchcancel", () => {
      this.onRightArrowRelease();
    });

    // Show/hide arrows
    ee.on("windowResize.throttled", () => {
      this.onWindowResize();
    });
    this.onWindowResize();
  },

  onWindowResize() {
    let items = toArray(this.menuList.querySelectorAll(".c-scroll-menu__item"));
    items = items.map(item => {
      return item.offsetWidth;
    });
    const itemsTotalWidth = items.reduce((accumulator, item) => {
      return accumulator + item;
    });
    const action = (itemsTotalWidth > this.menuNav.offsetWidth) ? "add" : "remove";
    this.rootElement.classList[action]("c-scroll-menu--overflowing");
  },

  cancelInterval() {
    this.moveSpeed = MOVE_START_SPEED;
    clearInterval(this.scrollInterval);
  },

  cancelClickTimeout() {
    clearTimeout(this.clickTimeout);
  },

  // -------------------
  // Left arrow
  // -------------------

  onLeftArrowTouch() {
    this.cancelInterval();
    this.mouseLongPress = false;
    this.mouseIsDown = true;
    this.clickTimeout = setTimeout(() => {
      this.leftClickTimeout();
    }, CLICK_THRESHOLD);
  },

  onLeftArrowRelease() {
    this.cancelInterval();
    this.cancelClickTimeout();
    if (this.mouseIsDown && !this.mouseLongPress) {
      this.moveMenuLeft();
      this.mouseLongPress = false;
    }
    this.mouseIsDown = false;
  },

  leftClickTimeout() {
    this.cancelClickTimeout();
    this.mouseLongPress = true;
    this.scrollInterval = setInterval(() => {
      this.scrollMenuLeft();
    }, INTERVAL_TIME);
  },

  scrollMenuLeft() {
    this.moveSpeed *= SPEED_MULTIPLIER;
    if (this.moveSpeed > MAX_MOVE_SPEED) {
      this.moveSpeed = MAX_MOVE_SPEED;
    }
    this.menuList.scrollLeft -= this.moveSpeed;
  },

  moveMenuLeft() {
    this.menuList.scrollLeft -= MENU_JUMP_VALUE;
  },

  // -------------------
  // Right arrow
  // -------------------

  onRightArrowTouch() {
    this.cancelInterval();
    this.mouseLongPress = false;
    this.mouseIsDown = true;
    this.clickTimeout = setTimeout(() => {
      this.rightClickTimeout();
    }, CLICK_THRESHOLD);
  },

  onRightArrowRelease() {
    this.cancelInterval();
    this.cancelClickTimeout();
    if (this.mouseIsDown && !this.mouseLongPress) {
      this.moveMenuRight();
      this.mouseLongPress = false;
    }
    this.mouseIsDown = false;
  },

  rightClickTimeout() {
    this.cancelClickTimeout();
    this.mouseLongPress = true;
    this.scrollInterval = setInterval(() => {
      this.scrollMenuRight();
    }, INTERVAL_TIME);
  },

  scrollMenuRight() {
    this.moveSpeed *= SPEED_MULTIPLIER;
    if (this.moveSpeed > MAX_MOVE_SPEED) {
      this.moveSpeed = MAX_MOVE_SPEED;
    }
    this.menuList.scrollLeft += this.moveSpeed;
  },

  moveMenuRight() {
    this.menuList.scrollLeft += MENU_JUMP_VALUE;
  }
};

export default ScrollMenuPrototype;
